import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Speed as SpeedIcon,
  Terminal as TerminalIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Memory as MemoryIcon,
  AccessTime as TimeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const RouterMonitorModal = ({ open, onClose, router }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tab 0: System Info States
  const [resources, setResources] = useState(null);

  // Tab 1: Traffic States
  const [interfaces, setInterfaces] = useState([]);
  const [selectedInterface, setSelectedInterface] = useState('');
  const [trafficData, setTrafficData] = useState([]);
  const [liveTraffic, setLiveTraffic] = useState({ rxRate: 0, txRate: 0 });
  const [isLiveActive, setIsLiveActive] = useState(true);

  // Tab 2: Logs States
  const [logs, setLogs] = useState([]);
  const [filterText, setFilterText] = useState('');

  const trafficIntervalRef = useRef(null);

  // Reset states when router or open state changes
  useEffect(() => {
    if (open && router) {
      setActiveTab(0);
      setError('');
      setResources(null);
      setInterfaces([]);
      setSelectedInterface('');
      setTrafficData([]);
      setLiveTraffic({ rxRate: 0, txRate: 0 });
      setLogs([]);
      setFilterText('');
      fetchResources();
    }
    return () => clearIntervals();
  }, [open, router]);

  // Handle Tab Change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    clearIntervals();

    if (newValue === 0) {
      fetchResources();
    } else if (newValue === 1) {
      fetchInterfaces();
    } else if (newValue === 2) {
      fetchLogs();
    }
  };

  const clearIntervals = () => {
    if (trafficIntervalRef.current) {
      clearInterval(trafficIntervalRef.current);
      trafficIntervalRef.current = null;
    }
  };

  // Fetch System Resources
  const fetchResources = async () => {
    if (!router) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/routers/${router.id}/monitor/resources`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Error al obtener recursos del router');
      }
      setResources(data);
    } catch (err) {
      setError(err.message || 'Error al conectar con el router');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Interfaces
  const fetchInterfaces = async () => {
    if (!router) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/routers/${router.id}/monitor/interfaces`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Error al obtener puertos del router');
      }
      setInterfaces(data.interfaces || []);
      // Auto select first physical interface
      if (data.interfaces && data.interfaces.length > 0) {
        const firstEth = data.interfaces.find(i => i.name.toLowerCase().includes('ether')) || data.interfaces[0];
        setSelectedInterface(firstEth.name);
      }
    } catch (err) {
      setError(err.message || 'Error al obtener interfaces');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Traffic Tick
  const fetchTrafficTick = async (interfaceName) => {
    if (!router || !interfaceName) return;
    try {
      const response = await fetch(`/api/routers/${router.id}/monitor/traffic?interface=${interfaceName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setLiveTraffic({ rxRate: data.rxRate, txRate: data.txRate });
        
        // Convert to Mbps for clean chart scale
        const rxMbps = data.rxRate / 1000000;
        const txMbps = data.txRate / 1000000;
        
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        
        setTrafficData(prev => {
          const newData = [...prev, { time: timestamp, download: rxMbps, upload: txMbps }];
          if (newData.length > 20) {
            newData.shift();
          }
          return newData;
        });
      }
    } catch (err) {
      console.error('Error polling traffic:', err);
    }
  };

  // Start polling traffic
  useEffect(() => {
    clearIntervals();
    if (activeTab === 1 && selectedInterface && isLiveActive && open) {
      setTrafficData([]);
      fetchTrafficTick(selectedInterface);
      trafficIntervalRef.current = setInterval(() => {
        fetchTrafficTick(selectedInterface);
      }, 2000);
    }
    return () => clearIntervals();
  }, [activeTab, selectedInterface, isLiveActive, open]);

  // Fetch Logs
  const fetchLogs = async () => {
    if (!router) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/routers/${router.id}/monitor/logs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Error al obtener logs');
      }
      setLogs(data.logs || []);
    } catch (err) {
      setError(err.message || 'Error al conectar con el router');
    } finally {
      setLoading(false);
    }
  };

  // Format bytes for memory
  const formatBytes = (bytes) => {
    if (bytes === 0 || !bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format traffic speed (bps)
  const formatSpeed = (bps) => {
    if (bps === undefined || bps === null) return '0 bps';
    if (bps < 1000) return `${bps} bps`;
    const kbps = bps / 1000;
    if (kbps < 1000) return `${kbps.toFixed(1)} Kbps`;
    const mbps = kbps / 1000;
    return `${mbps.toFixed(2)} Mbps`;
  };

  // Filter logs locally
  const filteredLogs = logs.filter(log => {
    if (!filterText) return true;
    const term = filterText.toLowerCase();
    return (
      (log.message && log.message.toLowerCase().includes(term)) ||
      (log.topics && log.topics.toLowerCase().includes(term)) ||
      (log.time && log.time.toLowerCase().includes(term))
    );
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SpeedIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
              Monitoreo del Router: {router?.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              IP: {router?.ip} ({router?.api_type === 'rest' || router?.api_type === 'rest-ssl' ? 'REST API' : 'API Socket'})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" textColor="primary" indicatorColor="primary">
          <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Información del Sistema" />
          <Tab icon={<SpeedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Tráfico en Tiempo Real" />
          <Tab icon={<TerminalIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Logs del Router" />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ height: '420px', overflow: 'hidden', p: 2, bgcolor: '#f8fafc' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, py: 0 }} action={
            <Button color="inherit" size="small" onClick={() => handleTabChange(null, activeTab)}>
              Reintentar
            </Button>
          }>
            {error}
          </Alert>
        )}

        {loading && !resources && !interfaces.length && !logs.length && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Conectando con el router MikroTik...
            </Typography>
          </Box>
        )}

        {/* Tab 0: Información del Sistema */}
        {activeTab === 0 && resources && (
          <Grid container spacing={2}>
            {/* CPU Load */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                      Carga de CPU
                    </Typography>
                    <SpeedIcon color="primary" fontSize="small" />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: '#3b82f6' }}>
                    {resources.cpuLoad}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={resources.cpuLoad}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: resources.cpuLoad > 80 ? '#ef4444' : resources.cpuLoad > 50 ? '#f59e0b' : '#3b82f6',
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Modelo: {resources.cpu}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Núcleos: {resources.cpuCount} ({resources.cpuFrequency} MHz)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* RAM Usage */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                      Memoria RAM
                    </Typography>
                    <MemoryIcon color="success" fontSize="small" />
                  </Box>
                  {(() => {
                    const used = resources.totalMemory - resources.freeMemory;
                    const percent = resources.totalMemory > 0 ? Math.round((used / resources.totalMemory) * 100) : 0;
                    return (
                      <>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: '#10b981' }}>
                          {percent}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: percent > 85 ? '#ef4444' : '#10b981',
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Libre: {formatBytes(resources.freeMemory)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Total: {formatBytes(resources.totalMemory)}
                          </Typography>
                        </Box>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>

            {/* System Details */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1.5 }}>
                    Detalles del RouterOS
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Modelo de Placa</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{resources.boardName}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Arquitectura</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{resources.architectureName}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Versión de RouterOS</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>v{resources.version}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                      <TimeIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>Tiempo de Actividad (Uptime)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.25 }}>{resources.uptime}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab 1: Tráfico en Tiempo Real */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header controls with compact chips */}
            <Paper variant="outlined" sx={{ p: 1, px: 1.5, borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="select-interface-label">Puerto</InputLabel>
                  <Select
                    labelId="select-interface-label"
                    value={selectedInterface}
                    label="Puerto"
                    onChange={(e) => setSelectedInterface(e.target.value)}
                  >
                    {interfaces.map(i => (
                      <MenuItem key={i.name} value={i.name}>
                        🔌 {i.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedInterface && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      icon={<DownloadIcon sx={{ fontSize: '0.9rem !important' }} />}
                      label={`RX: ${formatSpeed(liveTraffic.rxRate)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: '24px' }}
                    />
                    <Chip
                      icon={<UploadIcon sx={{ fontSize: '0.9rem !important' }} />}
                      label={`TX: ${formatSpeed(liveTraffic.txRate)}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: '24px' }}
                    />
                  </Box>
                )}
              </Box>

              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsLiveActive(!isLiveActive)}
                startIcon={isLiveActive ? <CloseIcon sx={{ fontSize: 14 }} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                color={isLiveActive ? 'error' : 'primary'}
                sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}
              >
                {isLiveActive ? 'Pausar' : 'Monitorear'}
              </Button>
            </Paper>

            {selectedInterface ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <Card sx={{ borderRadius: '12px', p: 1.5, height: '300px', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold', display: 'block' }}>
                    Consumo Histórico Reciente (Mbps)
                  </Typography>
                  <Box sx={{ flex: 1, width: '100%', height: '240px', minHeight: 0 }}>
                    {trafficData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trafficData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorDownload" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorUpload" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="time" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                          <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" unit="M" />
                          <RechartsTooltip
                            formatter={(value) => [`${(value * 1000).toFixed(0)} Kbps (${value.toFixed(2)} Mbps)`, '']}
                            contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.75rem', padding: '4px 8px' }}
                          />
                          <Legend verticalAlign="top" height={24} iconSize={10} wrapperStyle={{ fontSize: '0.75rem' }} formatter={(value) => value === 'download' ? 'Descarga (RX)' : 'Subida (TX)'} />
                          <Area
                            type="monotone"
                            dataKey="download"
                            stroke="#6366f1"
                            fillOpacity={1}
                            fill="url(#colorDownload)"
                            strokeWidth={1.5}
                            isAnimationActive={false}
                          />
                          <Area
                            type="monotone"
                            dataKey="upload"
                            stroke="#10b981"
                            fillOpacity={1}
                            fill="url(#colorUpload)"
                            strokeWidth={1.5}
                            isAnimationActive={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                        <Typography variant="caption" color="textSecondary">
                          Obteniendo datos de tráfico...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #94a3b8', borderRadius: '12px' }}>
                <Typography variant="body2" color="textSecondary">
                  Seleccione un puerto para ver el tráfico en tiempo real.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: Logs del Router */}
        {activeTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Filtrar logs..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ flex: 1, bgcolor: '#fff' }}
                InputProps={{
                  startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />,
                  style: { height: 32, fontSize: '0.8rem' }
                }}
              />
              <Button
                variant="outlined"
                onClick={fetchLogs}
                disabled={loading}
                startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none' }}
              >
                Actualizar
              </Button>
            </Box>

            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 1.5,
                bgcolor: '#1e1e1e',
                color: '#34d399',
                borderRadius: '8px',
                height: '320px',
                overflowY: 'auto',
                fontFamily: 'Consolas, Monaco, monospace',
                boxShadow: 'inset 0 0 10px #000',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#444', borderRadius: '3px' }
              }}
            >
              {filteredLogs.length === 0 ? (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', textAlign: 'center', mt: 3 }}>
                  No se encontraron logs.
                </Typography>
              ) : (
                filteredLogs.map((log, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      borderBottom: '1px solid #2d2d2d',
                      py: 0.5,
                      fontSize: '0.75rem',
                      lineHeight: 1.3
                    }}
                  >
                    {/* Timestamp */}
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        color: '#60a5fa',
                        width: { xs: 'auto', sm: '90px' },
                        flexShrink: 0,
                        fontSize: 'inherit'
                      }}
                    >
                      {log.time}
                    </Typography>

                    {/* Topics */}
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        color: '#f59e0b',
                        width: { xs: 'auto', sm: '150px' },
                        flexShrink: 0,
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'inherit',
                        pr: 1
                      }}
                    >
                      [{log.topics}]
                    </Typography>

                    {/* Message */}
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        color: log.message.toLowerCase().includes('error') || log.message.toLowerCase().includes('fail') ? '#f87171' : '#e2e8f0',
                        fontSize: 'inherit',
                        wordBreak: 'break-all'
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5, px: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouterMonitorModal;
